import {
  avgPrescribedEffortDisplayValue,
  deltaComparedToPrevious,
  formatInsightsVolumeDisplayWithSuffix,
  volumeDisplayValue,
} from "./insightsSetRowMath";

/** Overview row: numeric `value` + optional extra fields (e.g. `muscleGroupsPair`). */
export function buildInsightsOverviewMetric({ id, cur, prev = null, extra = {} }) {
  return {
    id,
    value: cur,
    delta: deltaComparedToPrevious(prev, cur),
    ...extra,
  };
}

/** Display row: formatted `valueDisplay` (per-lift cards, volume & coverage). */
export function buildInsightsDisplayMetric({
  id,
  cur,
  prev = null,
  formatDisplay,
  toDeltaScalar = (v) => v,
  extra = {},
}) {
  const prevDelta = prev == null ? null : toDeltaScalar(prev);
  return {
    id,
    valueDisplay: formatDisplay(cur),
    delta: deltaComparedToPrevious(prevDelta, toDeltaScalar(cur)),
    ...extra,
  };
}

/** Function to collect resolved exercises for a week.
 * @param {{ sessions?: { resolvedExercises?: object[] }[] }} weekResolution
 * @returns {object[]}
 */
export function collectResolvedExercisesForWeek(weekResolution) {
  const out = [];
  for (const session of weekResolution?.sessions ?? []) {
    for (const ex of session.resolvedExercises ?? []) {
      out.push(ex);
    }
  }
  return out;
}

/** Function to build lens comp footer payload.
 * @param {number} compVolKg
 * @param {(val: number, units?: string) => unknown} [fromMetric]
 * @param {"metric"|"imperial"} unitsKey
 * @param {string} insightsVolumeSuffix
 * @param {{ coverageVariant?: string, unprogrammedLiftIds?: string[], liftBalance?: object|null }} [options]
 */
export function buildLensCompFooterPayload(
  compVolKg,
  fromMetric,
  unitsKey,
  insightsVolumeSuffix,
  { coverageVariant, unprogrammedLiftIds = [], liftBalance = null } = {}
) {
  return {
    compVolumeKg: compVolKg,
    volumeDisplay: formatInsightsVolumeDisplayWithSuffix(
      compVolKg,
      fromMetric,
      unitsKey,
      insightsVolumeSuffix
    ),
    liftBalance,
    coverageVariant,
    unprogrammedLiftIds,
  };
}

/** Function to build week display scalars from scan.
 * @param {ReturnType<typeof import("./insightsWeekScan").scanWeekExercises>} scan
 * @param {(val: number, units?: string) => unknown} [fromMetric]
 * @param {"metric"|"imperial"} unitsKey
 */
export function weekDisplayScalarsFromScan(
  scan,
  fromMetric,
  unitsKey,
  intensityMeasure = "rpe"
) {
  return {
    exercises: scan.exerciseCount,
    totalSets: scan.totalSets,
    estimatedReps: scan.estimatedReps,
    muscleGroupsPair: scan.muscleGroupsPair,
    muscleGroupsValue: scan.muscleGroupsValue,
    avgPrescribedEffortDisplay: avgPrescribedEffortDisplayValue(
      scan.avgPrescribedRpe,
      intensityMeasure
    ),
    volumeDisplay: volumeDisplayValue(scan.volumeKg, fromMetric, unitsKey),
  };
}

/** Function to build overview metrics from display scalars.
 * @param {ReturnType<typeof weekDisplayScalarsFromScan>} cur
 * @param {ReturnType<typeof weekDisplayScalarsFromScan>|null} prev
 */
export function buildOverviewMetricsFromDisplayScalars(cur, prev) {
  return [
    buildInsightsOverviewMetric({
      id: "exercises",
      cur: cur.exercises,
      prev: prev?.exercises,
    }),
    buildInsightsOverviewMetric({
      id: "totalSets",
      cur: cur.totalSets,
      prev: prev?.totalSets,
    }),
    buildInsightsOverviewMetric({
      id: "estimatedReps",
      cur: cur.estimatedReps,
      prev: prev?.estimatedReps,
    }),
    buildInsightsOverviewMetric({
      id: "muscleGroups",
      cur: cur.muscleGroupsValue,
      extra: { muscleGroupsPair: cur.muscleGroupsPair },
    }),
    buildInsightsOverviewMetric({
      id: "avgPrescribedEffort",
      cur: cur.avgPrescribedEffortDisplay,
      prev: prev?.avgPrescribedEffortDisplay,
    }),
    buildInsightsOverviewMetric({
      id: "volumeKg",
      cur: cur.volumeDisplay,
      prev: prev?.volumeDisplay,
    }),
  ];
}
